import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { api, apiUrl, request } from '@/lib/api/client'
import { asList } from '@/lib/api/list'
import type { AgendaItem, Guest, OnlinePlatform } from './types'

/**
 * Toplantı ekleri ve kayıtları. Dosya 8 MB'lık parçalar hâlinde yüklenir (her parça sunucuda ayrı şifrelenir); kayıt için
 * müşteri onayı zorunlu. Oynatma/indirme kullanıcıya bağlı kısa ömürlü jetonla (her açılış izleme geçmişine yazılır).
 */
export const MEETING_FILE_PERMS = {
  attachmentUpload: 'crm:meeting:attachment_upload',
  attachmentView: 'crm:meeting:attachment_view',
  attachmentDelete: 'crm:meeting:attachment_delete',
  recordingUpload: 'crm:meeting:recording_upload',
  recordingView: 'crm:meeting:recording_view',
  recordingDownload: 'crm:meeting:recording_download',
  recordingDelete: 'crm:meeting:recording_delete',
  settings: 'crm:meeting:file_settings',
  audit: 'crm:meeting:file_audit',
} as const

export type FileKind = 'attachment' | 'recording'
export const CONSENT_METHODS = ['verbal', 'written', 'email', 'platform'] as const
export type ConsentMethod = (typeof CONSENT_METHODS)[number]
export type Consent = { contactId: string; by: string; method: ConsentMethod | ''; at: string; note: string }

export type MeetingFile = {
  id: string
  meetingId: string
  kind: FileKind
  name: string
  mime: string
  size: number
  status: 'uploading' | 'ready'
  note: string
  partCount: number
  uploadedParts: number
  consent: Consent | null
  uploadedBy: string
  createdAt: string
  expiresAt: string
  canDownload: boolean
  canDelete: boolean
}

export type FileSettings = { maxFileMb: number; recordingRetentionDays: number; attachmentRetentionDays: number; partSize: number }

type Row = Record<string, unknown>
const s = (v: unknown) => (v == null ? '' : String(v))

const mapFile = (r: Row): MeetingFile => {
  const c = r.consent && typeof r.consent === 'object' ? (r.consent as Row) : null
  return {
    id: s(r.id),
    meetingId: s(r.meeting_id),
    kind: r.kind === 'recording' ? 'recording' : 'attachment',
    name: s(r.name),
    mime: s(r.mime),
    size: Number(r.size) || 0,
    status: r.status === 'ready' ? 'ready' : 'uploading',
    note: s(r.note),
    partCount: Number(r.part_count) || 0,
    uploadedParts: Number(r.uploaded_parts) || 0,
    consent: c ? { contactId: s(c.contact_id), by: s(c.by), method: s(c.method) as ConsentMethod, at: s(c.at), note: s(c.note) } : null,
    uploadedBy: s(r.uploaded_by),
    createdAt: s(r.created_at),
    expiresAt: s(r.expires_at),
    canDownload: r.can_download === true,
    canDelete: r.can_delete === true,
  }
}

const filesKey = (meetingId: string) => ['crm', 'meeting-files', meetingId] as const

export const useMeetingFiles = (meetingId: string, enabled = true) =>
  useQuery({
    queryKey: filesKey(meetingId),
    enabled: enabled && !!meetingId,
    queryFn: async () => asList(await api.get<Row[]>(`/api/v1/meetings/${meetingId}/files`)).map(mapFile),
  })

export const useMeetingFileSettings = () =>
  useQuery({
    queryKey: ['crm', 'meeting-file-settings'],
    queryFn: async (): Promise<FileSettings> => {
      const r = await api.get<Row>('/api/v1/meeting-file-settings')
      return {
        maxFileMb: Number(r.max_file_mb) || 2048,
        recordingRetentionDays: Number(r.recording_retention_days) || 0,
        attachmentRetentionDays: Number(r.attachment_retention_days) || 0,
        partSize: Number(r.part_size) || 8 << 20,
      }
    },
  })

export const useSaveMeetingFileSettings = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: Omit<FileSettings, 'partSize'>) =>
      api.put('/api/v1/meeting-file-settings', {
        max_file_mb: v.maxFileMb,
        recording_retention_days: v.recordingRetentionDays,
        attachment_retention_days: v.attachmentRetentionDays,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm', 'meeting-file-settings'] }),
  })
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

/**
 * Parçalı yükleme: başlatır, parçaları sırayla gönderir (ağ hatasında 3 kez yeniden dener), tamamlar. İptalde (signal)
 * sunucudaki yarım yükleme silinir.
 */
export async function uploadMeetingFile(opts: {
  meetingId: string
  file: File
  kind: FileKind
  note: string
  consent?: Consent
  signal: AbortSignal
  onProgress: (sent: number, total: number) => void
}): Promise<MeetingFile> {
  const { meetingId, file, kind, signal } = opts
  const c = opts.consent
  const raw = await api.post<Row>(`/api/v1/meetings/${meetingId}/files`, {
      kind,
      name: file.name,
      size: file.size,
      mime: file.type,
      note: opts.note,
      consent: c ? { contact_id: c.contactId, by: c.by, method: c.method, at: c.at ? new Date(c.at).toISOString() : '', note: c.note } : undefined,
  })
  const begun = mapFile(raw)
  // Parça boyutu sunucudan (son parça daha küçük).
  const partSize = Number(raw.part_size) || 8 << 20
  try {
    for (let n = 1; n <= begun.partCount; n++) {
      const blob = file.slice((n - 1) * partSize, Math.min(n * partSize, file.size))
      for (let attempt = 1; ; attempt++) {
        try {
          await request('PUT', `/api/v1/meetings/${meetingId}/files/${begun.id}/parts/${n}`, { body: blob, signal })
          break
        } catch (e) {
          if (signal.aborted || attempt >= 3) throw e
          await sleep(1000 * attempt)
        }
      }
      opts.onProgress(Math.min(n * partSize, file.size), file.size)
    }
    return mapFile(await api.post<Row>(`/api/v1/meetings/${meetingId}/files/${begun.id}/complete`))
  } catch (e) {
    await api.del(`/api/v1/meetings/${meetingId}/files/${begun.id}`, {}).catch(() => undefined)
    throw e
  }
}

export const useDeleteMeetingFile = (meetingId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ fileId, reason }: { fileId: string; reason: string }) => api.del(`/api/v1/meetings/${meetingId}/files/${fileId}`, { reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: filesKey(meetingId) }),
  })
}

export const useInvalidateMeetingFiles = () => {
  const qc = useQueryClient()
  return (meetingId: string) => qc.invalidateQueries({ queryKey: filesKey(meetingId) })
}

/** Oynatıcı ya da indirme için jetonlu bağlantı (jeton alınırken yetki denetlenir ve izleme kaydı düşülür). */
export async function meetingFileUrl(meetingId: string, fileId: string, download: boolean) {
  const r = await api.post<Row>(`/api/v1/meetings/${meetingId}/files/${fileId}/token`, { download })
  return apiUrl(`/api/v1/meeting-files/stream/${s(r.token)}`)
}

export type FileView = { userId: string; action: 'stream' | 'download'; ip: string; at: string }

export const useMeetingFileViews = (meetingId: string, fileId: string | null) =>
  useQuery({
    queryKey: ['crm', 'meeting-file-views', fileId],
    enabled: !!fileId,
    queryFn: async () =>
      asList(await api.get<Row[]>(`/api/v1/meetings/${meetingId}/files/${fileId}/views`)).map(
        (r): FileView => ({ userId: s(r.user_id), action: r.action === 'download' ? 'download' : 'stream', ip: s(r.ip), at: s(r.at) }),
      ),
  })

export const revealPasscode = async (meetingId: string) => s((await api.post<Row>(`/api/v1/meetings/${meetingId}/online-passcode`)).passcode)

export type MeetingRecordInput = {
  agenda: AgendaItem[]
  guests: Guest[]
  onlinePlatform: OnlinePlatform | ''
  onlineUrl: string
  onlineCode: string
  /** undefined: şifre değişmez; '' : silinir. */
  onlinePasscode?: string
}

export const useSaveMeetingRecord = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ meetingId, input }: { meetingId: string; input: MeetingRecordInput }) =>
      api.put(`/api/v1/meetings/${meetingId}`, {
        agenda: input.agenda,
        guests: input.guests,
        online_platform: input.onlinePlatform,
        online_url: input.onlineUrl,
        online_code: input.onlineCode,
        ...(input.onlinePasscode === undefined ? {} : { online_passcode: input.onlinePasscode }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm', 'meetings'] }),
  })
}

export const fmtSize = (n: number) =>
  n >= 1 << 30 ? `${(n / (1 << 30)).toFixed(2)} GB` : n >= 1 << 20 ? `${(n / (1 << 20)).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`

/** Toplantı notu açılınca görüntüleme geçmişine yazılır (sunucu aynı kullanıcı için 10 dakikada bir kaydeder). */
export function useRecordMeetingView(meetingId: string, enabled: boolean) {
  useEffect(() => {
    if (!enabled || !meetingId) return
    void api.post(`/api/v1/meetings/${meetingId}/views`).catch(() => undefined)
  }, [meetingId, enabled])
}

// --- Tutanak (PDF/Word), dağıtım ve şablon ---

export const MINUTES_PERMS = {
  export: 'crm:meeting:export',
  distribute: 'crm:meeting:distribute',
  distributeCustomer: 'crm:meeting:distribute_customer',
  template: 'crm:meeting:template_manage',
} as const

export type MinutesVersion = 'internal' | 'customer'

export const downloadMinutes = (meetingId: string, version: MinutesVersion, format: 'pdf' | 'docx') =>
  api.download(`/api/v1/meetings/${meetingId}/minutes`, `Urunver_Tutanak.${format}`, { version, format })

export type MinutesSend = { id: string; sentBy: string; version: MinutesVersion; channel: 'mailbox' | 'system'; recipients: string[]; subject: string; createdAt: string }

const sendsKey = (meetingId: string) => ['crm', 'minutes-sends', meetingId] as const

export const useMinutesSends = (meetingId: string, enabled = true) =>
  useQuery({
    queryKey: sendsKey(meetingId),
    enabled: enabled && !!meetingId,
    queryFn: async () =>
      asList(await api.get<Row[]>(`/api/v1/meetings/${meetingId}/minutes/sends`)).map(
        (r): MinutesSend => ({
          id: s(r.id),
          sentBy: s(r.sent_by),
          version: r.version === 'customer' ? 'customer' : 'internal',
          channel: r.channel === 'system' ? 'system' : 'mailbox',
          recipients: Array.isArray(r.recipients) ? r.recipients.map(s) : [],
          subject: s(r.subject),
          createdAt: s(r.created_at),
        }),
      ),
  })

export type SendMinutesInput = {
  managers: boolean
  staffIds: string[]
  contactIds: string[]
  guestEmails: string[]
  extraEmails: string[]
  message: string
  format: 'pdf' | 'docx'
}

export const useSendMinutes = (meetingId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (i: SendMinutesInput) => {
      const r = await api.post<Row>(`/api/v1/meetings/${meetingId}/minutes/send`, {
        managers: i.managers,
        staff_ids: i.staffIds,
        contact_ids: i.contactIds,
        guest_emails: i.guestEmails,
        extra_emails: i.extraEmails,
        message: i.message,
        format: i.format,
      })
      return {
        internal: Array.isArray(r.internal) ? r.internal.map(s) : [],
        customer: Array.isArray(r.customer) ? r.customer.map(s) : [],
        internalChannel: s(r.internal_channel),
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: sendsKey(meetingId) }),
  })
}

export type MinutesTemplate = {
  title: string
  customerIntro: string
  internalFooter: string
  customerFooter: string
  showParticipants: boolean
  showAgenda: boolean
  showActions: boolean
  showSignatures: boolean
  showInternalNotes: boolean
}

export const useMinutesTemplate = () =>
  useQuery({
    queryKey: ['crm', 'minutes-template'],
    queryFn: async (): Promise<MinutesTemplate> => {
      const r = await api.get<Row>('/api/v1/minutes-template')
      return {
        title: s(r.title),
        customerIntro: s(r.customer_intro),
        internalFooter: s(r.internal_footer),
        customerFooter: s(r.customer_footer),
        showParticipants: r.show_participants === true,
        showAgenda: r.show_agenda === true,
        showActions: r.show_actions === true,
        showSignatures: r.show_signatures === true,
        showInternalNotes: r.show_internal_notes === true,
      }
    },
  })

export const useSaveMinutesTemplate = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (t: MinutesTemplate) =>
      api.put('/api/v1/minutes-template', {
        title: t.title,
        customer_intro: t.customerIntro,
        internal_footer: t.internalFooter,
        customer_footer: t.customerFooter,
        show_participants: t.showParticipants,
        show_agenda: t.showAgenda,
        show_actions: t.showActions,
        show_signatures: t.showSignatures,
        show_internal_notes: t.showInternalNotes,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm', 'minutes-template'] }),
  })
}
